import _extends from "@babel/runtime/helpers/extends";
var _PasswordShownIcon, _PasswordHiddenIcon;
import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import SvgIcon from '../../SvgIcon';
import { ICON_SIZE } from '../../constants';
import PasswordHiddenIcon from '@tourlane/iconography/Glyphs/Other/PasswordHidden';
import PasswordShownIcon from '@tourlane/iconography/Glyphs/Other/PasswordShown';
import FormItem from '../../Form/FormItem';
import FormLabel from '../../Form/FormLabel';
import { validateBadNumberInput } from './helpers';
import { useCombineRefs, useInOut, useInput } from '../../hooks';
import { useAutofill } from '../../hooks/useAutofill';
import FormWrapper from '../../Form/FormWrapper/FormWrapper';
import FormField from '../../Form/FormField/FormField';
import FormIconWrapper from '../../Form/FormIconWrapper/FormIconWrapper';
import FormIconRightWrapper from '../../Form/FormIconRightWrapper/FormIconRightWrapper';
import { isDefinedValue } from '../../helpers/defined';
import { getIconColor, getIconHoverColor, getInputButtonColor } from '../../helpers/icons';
import { minHeight } from '../../style/min-height';
import { INPUT_HEIGHT } from '../constants';
const TextArea = styled.textarea`
  ${minHeight(INPUT_HEIGHT)}
`;
const TextField = /*#__PURE__*/React.forwardRef(({
  resize,
  type = 'text',
  hideErrorOnFocus = true,
  error,
  errorProps,
  withAbsoluteError = false,
  withNoPaddingForError = false,
  label,
  className,
  onBlur,
  onFocus,
  disabled = false,
  withBottomMargin = false,
  withTopMargin = false,
  icon,
  iconRight,
  iconRightColor,
  shrinkPlaceholder = false,
  multiline = false,
  onChange,
  onInput,
  onValueChange,
  value,
  defaultValue,
  placeholder,
  hideIconRightOnFocus = false,
  withPrefix = false,
  withSuffix = false,
  fullWidth,
  truncateValue = false,
  isFocusedMode = false,
  ...props
}, forwardedRef) => {
  const Component = useMemo(() => multiline ? TextArea : 'input', [multiline]);
  const inputRef = useRef(null);
  const {
    setRef: setAutofillRef,
    isAutofilled
  } = useAutofill();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [internalValue, setInternalValue] = useInput(value, defaultValue ?? '', onValueChange);
  const setError = useCallback(newHasError => {
    if (newHasError !== hasError) {
      setHasError(newHasError);
    }
  }, [hasError]);
  const onBlurHandler = useCallback(e => {
    onBlur?.(e);
    if (hasError) {
      setHasError(false);
      if (!internalValue) {
        inputRef.current.value = '';
      } else {
        inputRef.current.value = internalValue;
      }
    }
  }, [onBlur, hasError, internalValue]);
  const [isFocused, handleFocus, handleBlur] = useInOut(onFocus, onBlurHandler);
  const inputType = useMemo(() => isPasswordVisible ? 'text' : type, [isPasswordVisible, type]);
  return /*#__PURE__*/React.createElement(FormItem, {
    id: props.id,
    name: props.name,
    label: label,
    error: error,
    errorProps: errorProps,
    className: className,
    showError: !isFocused || !hideErrorOnFocus,
    absoluteError: withAbsoluteError,
    fullWidth: fullWidth,
    withBottomMargin: withBottomMargin,
    withTopMargin: withTopMargin,
    withNoPaddingForError: withNoPaddingForError
  }, /*#__PURE__*/React.createElement(FormWrapper, {
    $error: error,
    $disabled: disabled,
    $focused: !!isFocused || isFocusedMode,
    $autofilled: isAutofilled,
    $withPrefix: withPrefix,
    $withSuffix: withSuffix,
    $withPersistError: !hideErrorOnFocus
  }, icon && /*#__PURE__*/React.createElement(FormIconWrapper, null, /*#__PURE__*/React.createElement(SvgIcon, {
    color: getInputButtonColor(isFocused || isFocusedMode, disabled, error, !hideErrorOnFocus),
    size: ICON_SIZE
  }, icon)), /*#__PURE__*/React.createElement(FormField, _extends({
    ref: useCombineRefs(forwardedRef, inputRef, setAutofillRef),
    $resize: resize,
    size: 1,
    as: Component,
    error: error,
    disabled: disabled,
    $withIcon: !!icon,
    $withRightIcon: !!(multiline && iconRight),
    $multiline: multiline,
    onFocus: handleFocus,
    onBlur: handleBlur,
    value: internalValue,
    onInput: e => {
      onInput?.(e);
      if (type === 'number') {
        if (validateBadNumberInput(e)) {
          setError(true);
        }
      }
    },
    onChange: e => {
      onChange?.(e);
      const value = e?.target?.value;
      if (type === 'number') {
        if (validateBadNumberInput(e)) {
          setError(true);
        } else {
          setError(false);
          setInternalValue(value);
        }
      } else {
        setInternalValue(value);
      }
    },
    placeholder: shrinkPlaceholder ? '' : placeholder,
    type: inputType,
    $withPrefix: withPrefix,
    $withSuffix: withSuffix,
    $truncateValue: truncateValue
  }, props)), iconRight && !(hideIconRightOnFocus && isFocused) && /*#__PURE__*/React.createElement(FormIconRightWrapper, {
    $multiline: multiline,
    disabled: disabled,
    color: iconRightColor || getInputButtonColor(isFocused, disabled, error, !hideErrorOnFocus),
    size: ICON_SIZE
  }, iconRight), !multiline && type === 'password' && /*#__PURE__*/React.createElement(FormIconRightWrapper, {
    pointer: !disabled,
    disabled: disabled,
    onClick: () => !disabled && setPasswordVisible(v => !v),
    color: getIconColor(disabled),
    colorHover: getIconHoverColor(disabled),
    size: ICON_SIZE
  }, isPasswordVisible ? _PasswordShownIcon || (_PasswordShownIcon = /*#__PURE__*/React.createElement(PasswordShownIcon, null)) : _PasswordHiddenIcon || (_PasswordHiddenIcon = /*#__PURE__*/React.createElement(PasswordHiddenIcon, null))), shrinkPlaceholder && placeholder && /*#__PURE__*/React.createElement(FormLabel, {
    withIcon: !!icon,
    withRightIcon: !!iconRight,
    focused: !!isFocused || isFocusedMode,
    multiline: multiline,
    disabled: disabled,
    error: !!error,
    shrink: !!(isFocused || isFocusedMode || isDefinedValue(internalValue)),
    placeholder: placeholder
  })));
});
export default TextField;